import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { COLOR_SYSTEM } from "../../design/design-system";
import Icon from "../../atoms/Icon";
import CheckBox from "../../atoms/CheckBox";
import { DrugProductLabel } from "../../../common/models/types";
import { buildCodeFilterOptions } from "../../../common/constants/drugLabelModules";

/** 필터 대상 약 1개 (코드 옵션 생성을 위해 productLabels 동반) */
export interface FilterDrugOption {
  itemCode: string;
  productName: string;
  labels: DrugProductLabel[];
}

/** 커밋되는 필터 값. itemCodes: null=전체/[]=해제, codes: null=전체/부분 배열 */
export interface FilterValue {
  itemCodes: string[] | null;
  codes: string[] | null;
}

interface ReferenceFilterBarProps {
  drugs: FilterDrugOption[];
  /** 코드 라벨용 탭별 모듈명 맵(건기식 HFF) */
  moduleNameMap?: Record<string, string>;
  selectedItemCodes: string[] | null;
  selectedCodes: string[] | null;
  onApply: (next: FilterValue) => void;
}

/**
 * 제품 선택 토글 다음 상태. (제품은 []=전체 해제 허용)
 * - 전부 선택은 전체(null)로 정규화. 빈 배열([])은 유지.
 */
export function nextSelection(
  selected: string[] | null,
  itemCode: string,
  allItemCodes: string[]
): string[] | null {
  const base = selected ?? allItemCodes;
  const next = base.includes(itemCode)
    ? base.filter((c) => c !== itemCode)
    : [...base, itemCode];
  return next.length === allItemCodes.length ? null : next;
}

const ToggleBtn = styled.button<{ $active: boolean }>`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 48px;
  padding: 0 14px;
  border: 1px solid
    ${({ $active }) =>
      $active ? COLOR_SYSTEM.get("Skyblue")?.[300] : COLOR_SYSTEM.get("Gray")?.[50]};
  background: white;
  color: ${({ $active }) =>
    $active ? COLOR_SYSTEM.get("Skyblue")?.[600] : COLOR_SYSTEM.get("Gray")?.[700]};
  font-size: 13px;
  font-weight: 500;
  border-radius: 12px;
  cursor: pointer;
  white-space: nowrap;
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const ModalBox = styled.div`
  width: 560px;
  max-width: calc(100vw - 48px);
  max-height: 80vh;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};
`;

const ModalTitle = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
`;

const ModalDesc = styled.p`
  margin: 5px 0 0;
  font-size: 12px;
  line-height: 1.55;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
`;

const CloseBtn = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  border: none;
  background: none;
  padding: 2px;
  cursor: pointer;
`;

/** 좌(제품) | 우(코드) 2단. 높이를 묶어 각 컬럼이 독립 스크롤되게 한다 */
const Columns = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const Column = styled.div<{ $divider?: boolean }>`
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  padding: 10px 0;
  ${({ $divider }) =>
    $divider ? `border-right: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};` : ""}
`;

const ColDesc = styled.div`
  padding: 0 16px 8px;
  font-size: 11px;
  color: ${COLOR_SYSTEM.get("Gray")?.[400]};
`;

const Row = styled.div`
  padding: 6px 16px;
`;

const EmptyCol = styled.div`
  padding: 20px 16px;
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[400]};
`;

const Footer = styled.div`
  flex-shrink: 0;
  background: white;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 10px 16px;
  border-top: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};
`;

const ApplyHint = styled.span`
  margin-right: auto;
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Red")?.[500]};
`;

const ApplyBtn = styled.button`
  border: none;
  background: ${COLOR_SYSTEM.get("Skyblue")?.[500]};
  color: white;
  font-size: 14px;
  font-weight: 600;
  padding: 8px 20px;
  border-radius: 8px;
  cursor: pointer;
  &:disabled {
    background: ${COLOR_SYSTEM.get("Gray")?.[200]};
    color: ${COLOR_SYSTEM.get("Gray")?.[400]};
    cursor: default;
  }
`;

/** 컬럼 헤더(제목 + 전체선택/해제) */
const ColHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 16px 2px;
`;

const ColTitleText = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 10px;
`;

const HeaderLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  font-size: 11px;
  color: ${COLOR_SYSTEM.get("Skyblue")?.[600]};
  cursor: pointer;
  &:disabled {
    color: ${COLOR_SYSTEM.get("Gray")?.[300]};
    cursor: default;
  }
`;

/** 약명/항목명 ellipsis (CheckBox 루트 className으로 너비 제한) */
const NameCheck = styled(CheckBox)`
  display: flex;
  width: 100%;
  max-width: 100%;
  div {
    flex-shrink: 0;
  }
  & > span.name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`;

const setsEqual = (a: string[], b: string[]) =>
  a.length === b.length && a.every((x) => b.includes(x));

/**
 * 근거자료 통합 필터 — "검색 범위" 결정. 검색행의 '필터' 버튼 → 중앙 모달("필터 설정").
 * 모달 안 좌(제품) | 우(코드) 2단, draft(임시 선택) + [적용] 1회 커밋(D9~D17).
 * 모달은 body 포털 + position:fixed라 패널 폭/overflow에 종속되지 않는다.
 * 코드 옵션은 draft 제품 기준 corpus 실재만, 한글 라벨 단위(buildCodeFilterOptions).
 */
const ReferenceFilterBar: React.FC<ReferenceFilterBarProps> = ({
  drugs,
  moduleNameMap,
  selectedItemCodes,
  selectedCodes,
  onApply,
}) => {
  const [open, setOpen] = useState(false);
  const [draftItems, setDraftItems] = useState<string[] | null>(selectedItemCodes);
  const [draftCodes, setDraftCodes] = useState<string[] | null>(selectedCodes);

  const allItemCodes = useMemo(() => drugs.map((d) => d.itemCode), [drugs]);
  const sortedDrugs = useMemo(
    () => [...drugs].sort((a, b) => a.productName.localeCompare(b.productName, "ko")),
    [drugs]
  );

  // 코드 옵션 = draft 제품 기준 corpus의 한글 라벨 그룹(D12)
  const codeOptions = useMemo(() => {
    const draftDrugs = draftItems
      ? drugs.filter((d) => draftItems.includes(d.itemCode))
      : drugs;
    const corpus = draftDrugs.flatMap((d) => d.labels || []);
    return buildCodeFilterOptions(corpus, moduleNameMap);
  }, [drugs, draftItems, moduleNameMap]);
  const allCodes = useMemo(() => codeOptions.flatMap((o) => o.codes), [codeOptions]);

  // ESC = 취소(닫기) + 열려 있는 동안 배경 스크롤 잠금
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  const total = drugs.length;
  const isActive = selectedItemCodes !== null || selectedCodes !== null;
  const showProductPane = total > 1;
  if (total === 0) return null;

  const openModal = () => {
    setDraftItems(selectedItemCodes);
    setDraftCodes(selectedCodes);
    setOpen(true);
  };

  // 제품 토글
  const toggleItem = (itemCode: string) =>
    setDraftItems(nextSelection(draftItems, itemCode, allItemCodes));
  const checkedItem = (itemCode: string) =>
    draftItems === null || draftItems.includes(itemCode);

  // 코드 토글 (라벨 단위). 전부 선택은 전체(null)로 정규화. 빈([])은 전체 해제로 유지.
  const isAllCodes = draftCodes === null;
  const toggleCode = (codes: string[]) => {
    const base = draftCodes ?? allCodes;
    const has = codes.every((c) => base.includes(c));
    const next = has
      ? base.filter((c) => !codes.includes(c))
      : Array.from(new Set([...base, ...codes]));
    setDraftCodes(setsEqual(next, allCodes) ? null : next);
  };
  const checkedCode = (codes: string[]) =>
    isAllCodes || codes.every((c) => draftCodes!.includes(c));

  // 한쪽이라도 '전체 해제(빈 선택)'면 표시할 게 없어 적용 비활성
  const itemsEmpty = draftItems !== null && draftItems.length === 0;
  const codesEmpty = draftCodes !== null && draftCodes.length === 0;
  const applyDisabled = itemsEmpty || codesEmpty;

  const apply = () => {
    if (applyDisabled) return;
    // 코드: draft를 현재 옵션과 교집합으로 정리(stale 제거). 전부/빈(교집합 결과)이면 전체(null)로.
    // (빈 selectedCodes가 커밋돼 모든 섹션이 사라지는 것을 방지 — 의도적 '전체 해제'는 applyDisabled가 차단)
    let codes: string[] | null = null;
    if (draftCodes !== null) {
      const valid = draftCodes.filter((c) => allCodes.includes(c));
      codes = valid.length === 0 || setsEqual(valid, allCodes) ? null : valid;
    }
    onApply({ itemCodes: draftItems, codes });
    setOpen(false);
  };

  const summary = [
    selectedItemCodes !== null ? `약 ${selectedItemCodes.length}/${total}` : null,
    selectedCodes !== null ? "항목 선택됨" : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <>
      <ToggleBtn
        $active={isActive}
        onClick={openModal}
        title="검색·표시할 약과 항목을 선택합니다 (검색 범위)"
      >
        <Icon name="Filter" size={13} color={COLOR_SYSTEM.get("Gray")?.[500]} />
        필터{isActive && summary ? ` · ${summary}` : ""}
      </ToggleBtn>

      {open &&
        createPortal(
          <Overlay onClick={() => setOpen(false)}>
            <ModalBox onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
              <ModalHeader>
                <div>
                  <ModalTitle>필터 설정</ModalTitle>
                  <ModalDesc>
                    선택한 약·항목만 근거자료 패널에 표시됩니다.
                    <br />
                    보고서의 근거 코드나 처방전의 약을 클릭하면 필터가 해제된 뒤 해당 위치로
                    이동합니다.
                  </ModalDesc>
                </div>
                <CloseBtn onClick={() => setOpen(false)} aria-label="닫기">
                  <Icon name="X" size={18} color={COLOR_SYSTEM.get("Gray")?.[500]} />
                </CloseBtn>
              </ModalHeader>

              <Columns>
                {showProductPane && (
                  <Column $divider>
                    <ColHead>
                      <ColTitleText>제품(약)</ColTitleText>
                      <HeaderActions>
                        <HeaderLink
                          onClick={() => setDraftItems(null)}
                          disabled={draftItems === null}
                        >
                          전체선택
                        </HeaderLink>
                        <HeaderLink onClick={() => setDraftItems([])} disabled={itemsEmpty}>
                          전체해제
                        </HeaderLink>
                      </HeaderActions>
                    </ColHead>
                    <ColDesc>제품명 가나다순</ColDesc>
                    {sortedDrugs.map((d) => (
                      <Row key={d.itemCode}>
                        <NameCheck
                          size="Small"
                          checked={checkedItem(d.itemCode)}
                          onClick={() => toggleItem(d.itemCode)}
                        >
                          <span className="name" title={d.productName}>
                            {d.productName}
                          </span>
                        </NameCheck>
                      </Row>
                    ))}
                  </Column>
                )}
                <Column>
                  <ColHead>
                    <ColTitleText>허가 항목</ColTitleText>
                    <HeaderActions>
                      <HeaderLink
                        onClick={() => setDraftCodes(null)}
                        disabled={isAllCodes || codeOptions.length === 0}
                      >
                        전체선택
                      </HeaderLink>
                      <HeaderLink
                        onClick={() => setDraftCodes([])}
                        disabled={codesEmpty || codeOptions.length === 0}
                      >
                        전체해제
                      </HeaderLink>
                    </HeaderActions>
                  </ColHead>
                  <ColDesc>허가사항 기재순</ColDesc>
                  {codeOptions.length === 0 ? (
                    <EmptyCol>표시할 항목이 없습니다.</EmptyCol>
                  ) : (
                    codeOptions.map((opt) => (
                      <Row key={opt.label}>
                        <NameCheck
                          size="Small"
                          checked={checkedCode(opt.codes)}
                          onClick={() => toggleCode(opt.codes)}
                        >
                          <span className="name" title={opt.label}>
                            {opt.label}
                          </span>
                        </NameCheck>
                      </Row>
                    ))
                  )}
                </Column>
              </Columns>

              <Footer>
                {applyDisabled && <ApplyHint>표시할 약/항목을 1개 이상 선택하세요</ApplyHint>}
                <ApplyBtn onClick={apply} disabled={applyDisabled}>
                  적용
                </ApplyBtn>
              </Footer>
            </ModalBox>
          </Overlay>,
          document.body
        )}
    </>
  );
};

export default ReferenceFilterBar;
